import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AlertController, IonicModule, ModalController, NavController } from '@ionic/angular';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { NgxTippyModule } from 'ngx-tippy-wrapper';

import { AutoresponderComponent } from 'src/app/components/autoresponder/autoresponder.component';
import { ChannelSelectorModule } from 'src/app/components/channel-selector/channel-selector.module';
import { SupportStepsModule } from 'src/app/components/support-steps/support-steps.module';
import {
  TicketIntakeConfig,
  TicketIntakeVisualizerComponent,
} from 'src/app/components/ticket-intake-visualizer/ticket-intake-visualizer.component';
import { WorkingHoursComponent } from 'src/app/components/working-hours/working-hours.component';
import { WorkingHoursModule } from 'src/app/components/working-hours/working-hours.module';
import {
  DEFAULT_INITIAL_SUPPORT_STEP,
  EXTERNAL_CHAT_CONNECTION_TYPES,
  FEATURES,
  INBOX_TYPES,
  routes,
} from 'src/app/constants';
import { generateRandomString, removeUndefinedFields } from 'src/app/utils/utils';
import { InboundEmailRule } from 'src/models';
import { AGENT_HANDOFF_MODES, INTAKE_MODES } from 'src/models/channels';
import { Project } from 'src/models/projects';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { ApiService } from 'src/services/api.service';
import { AuthService } from 'src/services/auth.service';
import { InboundEmailrulesService } from 'src/services/inbound-email-rules.service';
import { MessageService } from 'src/services/message.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

@Component({
  selector: 'app-edit-email-channel',
  templateUrl: './edit-email-channel.component.html',
  styleUrls: ['./edit-email-channel.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IonicModule,
    NgxDatatableModule,
    SharedPipesModule,
    SupportStepsModule,
    NgxTippyModule,
    WorkingHoursModule,
    ChannelSelectorModule,
    AutoresponderComponent,
    TicketIntakeVisualizerComponent,
    RouterModule,
  ],
})
export class EditEmailChannelComponent implements OnInit {
  featureEnabled = true;
  blockedEmailField: string;
  validEmailDomains: any[] = [];
  emailRule: Partial<InboundEmailRule>;
  isUpdatingInbox = false;
  customerId: string;
  project?: Partial<Project>;
  hideProject?: boolean;
  ticketIntakeConfig: TicketIntakeConfig;

  constructor(
    public authService: AuthService,
    private msgSrvc: MessageService,
    private alertCtrl: AlertController,
    private modalCtrl: ModalController,
    private apiService: ApiService,
    private inboundEmailRulesService: InboundEmailrulesService,
    private navController: NavController,
    private route: ActivatedRoute,
    private searchablePopoverService: SearchablePopoverService,
  ) {}

  async ngOnInit() {
    this.featureEnabled = !this.authService.restrictedFeatures.includes(FEATURES.INBOUND_EMAIL);

    await this.loadEmailDomains();

    if (this.customerId) {
      this.getInboundEmailRule();
    } else {
      if (this.authService.selectedProject) {
        this.project = this.authService.selectedProject;
        this.hideProject = true;
      } else {
        const projectId = this.route.snapshot.params['projectId'] ?? this.authService.tenant.defaultProjectId;
        this.project = this.authService.projects.find((p) => p.id === projectId) as Project;
        this.hideProject = this.authService.projects.length === 1;
      }

      this.emailRule = {
        supportSteps: [DEFAULT_INITIAL_SUPPORT_STEP],
        name: '',
        email: '',
        channelId: '',
        shortId: generateRandomString(6),
        alwaysUseGenericEmailAddress: false,
        autoresponder: { enabled: false, condition: 'always', message: '' },
        projectId: this.project?.id,
      };
    }

    this.initializeTicketIntakeConfig();
  }

  async loadEmailDomains() {
    const emailDomainsResponse: any = await this.apiService.postPromise('/email-domains/list', {
      limit: null,
    });

    const domains = emailDomainsResponse.data ?? [];

    this.validEmailDomains = domains.filter((d) => d.valid);
  }

  getForwardEmail(rule: Partial<InboundEmailRule>) {
    return `${this.authService.tenantId}+${rule.shortId}@mail.unthread.io`;
  }

  async getInboundEmailRule() {
    const result = await this.inboundEmailRulesService.list({
      where: [{ field: 'customerId', operator: '==', value: this.customerId }],
    });

    if (result.data.length) {
      this.emailRule = result.data[0];
      this.emailRule.supportSteps = this.emailRule.customer.supportSteps;
      this.initializeTicketIntakeConfig();
    } else {
      this.msgSrvc.showError('No inbound email rule found');
    }
  }

  copyToClipboard(rule: Partial<InboundEmailRule>) {
    navigator.clipboard
      .writeText(this.getForwardEmail(rule))
      .then(() => {
        this.msgSrvc.show('Copied!');
      })
      .catch(() => {
        // Unable to copy
      });
  }

  async editWorkingHours() {
    const modal = await this.modalCtrl.create({
      component: WorkingHoursComponent,
      componentProps: {
        originalWorkingHours: this.authService.tenant?.workingHours ?? {},
        onSave: async (updates: any) => this.authService.updateTenant(updates),
      },
    });

    modal.present();
  }

  async save() {
    try {
      const payload: Partial<InboundEmailRule> = removeUndefinedFields(this.emailRule);

      this.isUpdatingInbox = true;

      const _result = payload?.id
        ? await this.inboundEmailRulesService.update(payload.id, payload)
        : await this.inboundEmailRulesService.create(payload);

      this.msgSrvc.show('✔︎ Saved');
      this.isUpdatingInbox = false;

      this.dismiss();
    } catch (e) {
      this.msgSrvc.showError(e);
      this.isUpdatingInbox = false;
    }
  }

  async delete() {
    const alert = await this.alertCtrl.create({
      header: 'Disable email forwarding',
      subHeader: 'Are you sure you want to disable email forwarding?',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel',
        },
        {
          text: 'Yes',
          handler: async () => {
            if (this.emailRule) {
              this.isUpdatingInbox = true;

              await this.inboundEmailRulesService.delete(this.emailRule.id);

              this.dismiss();

              this.isUpdatingInbox = false;

              this.navController.navigateRoot(
                `${routes.DASHBOARD}/${routes.CHANNELS}/${EXTERNAL_CHAT_CONNECTION_TYPES.EMAIL}`,
              );
            }
          },
        },
      ],
    });

    await alert.present();
  }

  dismiss() {
    this.modalCtrl.dismiss();
  }

  addBlockedEmail() {
    if (!this.blockedEmailField || this.blockedEmailField.trim() === '' || !this.blockedEmailField.includes('.')) {
      return;
    }

    if (this.emailRule.blocklist) {
      this.emailRule.blocklist.push(this.blockedEmailField.toLowerCase());
    } else {
      this.emailRule.blocklist = [this.blockedEmailField.toLowerCase()];
    }

    this.blockedEmailField = '';
  }

  deleteBlocklistEntry(index: number) {
    this.emailRule.blocklist = this.emailRule.blocklist.filter((_, i) => i !== index);
  }

  showEmailHelpdesk() {
    window.open('https://docs.unthread.io/email/email-slack-connector', '_blank');
  }

  viewInbox() {
    this.navController.navigateRoot(`${routes.DASHBOARD}/${routes.INBOX}/${INBOX_TYPES.EMAIL}/${this.emailRule.id}`);
  }

  onRuleChannelChanged(slackChannel) {
    this.emailRule.channelId = slackChannel?.id ?? null;
    this.emailRule.slackChannel = slackChannel;

    if (this.emailRule.id) {
      this.save();
    }
  }

  sortBlocklist(): string[] {
    if (!this.emailRule?.blocklist) {
      return [];
    }

    // Safely copy and sort so we don't mutate the original array
    return Array.from(
      new Set([...this.emailRule.blocklist].map((b) => b.toLowerCase().trim()).sort((a, b) => a.localeCompare(b))),
    );
  }

  selectProject() {
    this.searchablePopoverService.projects({
      event,
      showNoneOption: false,
      selectedProjectId: this.project?.id,
      callback: async (_id: string, _name: string, object: any) => {
        this.project = object;
      },
    });
  }

  initializeTicketIntakeConfig() {
    this.ticketIntakeConfig = {
      intakeSettings: {
        bot: {
          enabled: false,
          maxResponses: null,
          handoffMode: AGENT_HANDOFF_MODES.PROMPT,
        },
        form: {
          enabled: false,
        },
      },
      overrideIntakeSettings: false,
      intakeMode: INTAKE_MODES.IMMEDIATE,
      automatedTicketingEnabled: true,
      supportSteps: this.emailRule?.supportSteps || [DEFAULT_INITIAL_SUPPORT_STEP],
      autoresponder: this.emailRule?.autoresponder || { enabled: false, condition: 'always', message: '' },
      supportStepRulesetId: undefined,
    };
  }

  onTicketIntakeConfigChange(config: TicketIntakeConfig) {
    this.ticketIntakeConfig = config;

    // Update email rule with new values
    if (config.supportSteps) {
      this.emailRule.supportSteps = config.supportSteps;
    }

    if (config.autoresponder) {
      this.emailRule.autoresponder = config.autoresponder;
    }
  }
}
